import { gql, useQuery } from '@apollo/client'

import agentClient from '@/lib/apollo/client/agent'
import type { WrappedUseQuery } from '@/lib/apollo/types'

export const getDocumentRequestsQuery = gql`
  query (
    $customerId: String!
    $applicationId: String!
    $subjectId: String!
    $sectionNames: [SectionName]
  ) {
    getDocumentRequests(
      customerId: $customerId
      applicationId: $applicationId
      subjectId: $subjectId
      sectionNames: $sectionNames
    ) {
      id
      createdAt
      updatedAt
      author
      requestMessage
      completed
      documentType
      documentsUploaded
      additionalContext
      documentSection
    }
  }
`

export enum SectionName {
  identity = 'identity',
  credit = 'credit',
  income = 'income',
  residential = 'residential',
  organisation = 'organisation',
  financial = 'financial',
}

export enum DocumentType {
  mrzMissing = 'MRZ missing',
  shareCode = 'Share code',
  selfie = 'Selfie',
  imageQuality = 'Image quality',
  proofOfNameChange = 'Proof of name change',
  inDateDrivingLicence = 'In-date driving licence',
  supplimentaryId = 'Supplimentary ID',
  guarantorPaperIdDocumentsProvided = 'GG - Photo ID required',
  idDoc = 'ID document required',
  bothSidesOfPhotoId = 'Both sides of photo ID',
  letterOfAuthority = 'Letter of authority',
  appropriateId = 'Appropriate ID',
  freeText = 'Free text',
  other = 'Other',
  promptReferee = 'Reminder to prompt referee',
  unacceptableReferee = 'Referee not acceptable',
  newLandlordRefereeDetails = 'New landlord referee details',
  tenancyAgreement = 'Tenancy agreement',
  tenancyAgreementAndBankStatements = 'Tenancy agreement & bank statements',
  bankStatements = 'Bank statements',
  partialBankStatement = 'Partial bank statement',
  residentialRefereeContactDetails = 'Referee contact details',
  docsRequestAwaitingLandlordRef = 'Docs requested while awaiting landlord reference',
  outstandingResidentialDocument = 'Outstanding Residential document required',
  proofOfSatisfiedAdverseCredit = 'Proof of satisfied adverse credit',
  outstandingCreditDocument = 'Outstanding Credit document required',
  outstandingIncomeDocument = 'Outstanding Information Required',
  incompletePayslips = '(PAYE) Incomplete payslips',
  partialPayslips = '(PAYE) Partial payslips',
  ftcInformation = '(PAYE) FTC information',
  incompleteContract = '(PAYE) Incomplete contract',
  employmentContractLessThan3Months = '(PAYE) Employment contract < 3 months',
  internationalEmployment = '(PAYE) International employment',
  noIncomeOnOpenBanking = '(PAYE) No income in open banking',
  futureEmployment = '(PAYE) Future employment',
  employmentContract = '(PAYE) Offer letter/ employment contract',
  payePromptReferee = '(PAYE) Prompt referee',
  payeRefereeContactDetails = '(PAYE) Referee contact details',
  payeSickPay = '(PAYE) Sick pay',
  subcontractorDocuments = '(Other) Subcontractor documents',
  taxReturnWithProofOfSubmission = '(Other) Tax return with proof of submission',
  taxReturnOnly = '(Other) Tax return only',
  investments = '(Other) Investments',
  insufficientBankStatement = '(Other) Insufficient Bank Statement',
  studentLoan = '(Other) Student Loan',
  benefits = '(Other) Benefits',
  incompletePensionDoc = '(Other) Incomplete pension doc',
  jointAccountPension = '(Other) Joint account pension',
  proofOfSubmission = '(Other) Proof of submission',
  maternityLeave = '(Other) Maternity leave',
  dividendsConfirmation = '(Other) Dividends Confirmation',
  thirdPartyConfirmation = '(Other) Third party confirmation',
  nonUKSelfEmployment = '(Other) Non UK self employment',
  selfEmployedLessThan1Year = '(Other) Self employed < 1 year',
  incompleteOldTaxReturn = '(Other) Incomplete/ Old Tax return',
  accountantsDetails = '(Other) Accountants details',
  accountantPrompt = '(Other) Accountant prompt',
  universalCredit = '(Other) Universal Credit',
  obTsrSecondChance = '(Other) OB TSR second chance',
  konfirTsrSecondChance = '(Other) Konfir TSR second chance',
  completeIdVerification = 'Complete your ID Verification',
  completeIdVerificationImageQuality = 'Complete your ID Verification (Image quality)',
  completeIdVerificationPartialImage = 'Complete your ID Verification (Partial image received)',
  completeIdVerificationUnsupportedDocument = 'Complete your ID Verification (Unsupported document)',
  completeIdVerificationFaceCheckRetry = 'Complete your ID Verification (Face check retry required)',
}

export interface DocumentRequest {
  id: string
  createdAt: string
  updatedAt: string
  author: string
  requestMessage: string
  completed: boolean
  documentType: string
  documentsUploaded?: string[]
  additionalContext?: string
  documentSection: SectionName
}

export type QueryVars = {
  customerId: string
  applicationId: string
  subjectId: string
  sectionNames?: SectionName[]
}

export type GetDocumentRequestsData = {
  getDocumentRequests: DocumentRequest[]
}

export const useGetDocumentRequestsQuery: WrappedUseQuery<
  GetDocumentRequestsData,
  QueryVars
> = options =>
  useQuery<GetDocumentRequestsData, QueryVars>(getDocumentRequestsQuery, {
    ...options,
    client: agentClient,
  })
